<template>
  <v-container fluid class="d-flex justify-center" :style="`margin-left: ${margin}`">
    <div style="width: 100%; max-width: 1200px;">

      <!-- Header -->
      <div class="table-header-filters pa-4 mb-6">
        <v-row dense align="center">
          <v-col cols="12" sm="auto">
            <div class="period-pills">
              <button
                v-for="opt in periods"
                :key="opt.value"
                class="period-pill"
                :class="{ active: period === opt.value }"
                @click="selectPeriod(opt.value)"
              >
                {{ opt.label }}
              </button>
            </div>
          </v-col>

          <v-col v-if="period === 'custom'" cols="12" sm="auto" class="d-flex align-center" style="gap: 6px;">
            <v-menu v-model="menuFrom" offset-y :close-on-content-click="false" min-width="auto">
              <template v-slot:activator="{ on, attrs }">
                <button class="period-pill date-pill" v-bind="attrs" v-on="on">
                  <v-icon x-small class="mr-1">mdi-calendar-start</v-icon>
                  {{ formatDate(customFrom) }}
                </button>
              </template>
              <v-date-picker v-model="customFrom" no-title scrollable @input="menuFrom = false; fetchData()" />
            </v-menu>
            <span class="date-pill-sep">—</span>
            <v-menu v-model="menuTo" offset-y :close-on-content-click="false" min-width="auto">
              <template v-slot:activator="{ on, attrs }">
                <button class="period-pill date-pill" v-bind="attrs" v-on="on">
                  <v-icon x-small class="mr-1">mdi-calendar-end</v-icon>
                  {{ formatDate(customTo) }}
                </button>
              </template>
              <v-date-picker v-model="customTo" no-title scrollable @input="menuTo = false; fetchData()" />
            </v-menu>
          </v-col>

          <v-col cols="auto" class="ml-auto">
            <v-btn icon :to="{ name: `${targetModifier}.all-reports.reportMenu` }" class="close-btn">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-col>
        </v-row>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="text-center py-12">
        <v-progress-circular indeterminate color="cyan darken-1" size="48" />
        <div class="mt-4 grey--text">Učitavanje podataka...</div>
      </div>

      <template v-else>

        <!-- KPI kartice 2×2 -->
        <v-row class="mb-6">
          <v-col cols="6">
            <v-card outlined rounded="lg" class="pa-4 text-center">
              <div class="kpi-label">Plaćeni računi</div>
              <div class="kpi-value" style="color: #4CAF50">{{ paidCount }}</div>
              <div class="kpi-sub">{{ formatEur(paidEuros) }}</div>
            </v-card>
          </v-col>

          <v-col cols="6">
            <v-card outlined rounded="lg" class="pa-4 text-center">
              <div class="kpi-label">Stornirani</div>
              <div class="kpi-value" style="color: #F44336">{{ stornoCount }}</div>
              <div class="kpi-sub">{{ formatEur(stornoEuros) }}</div>
            </v-card>
          </v-col>

          <v-col cols="6">
            <v-card outlined rounded="lg" class="pa-4 text-center">
              <div class="kpi-label">Storno %</div>
              <div class="kpi-value" :style="{ color: stornoPercent > 5 ? '#F44336' : '#4CAF50' }">
                {{ stornoPercent.toFixed(1) }}%
              </div>
              <div class="kpi-sub">od ukupno {{ totalCount }}</div>
            </v-card>
          </v-col>

          <v-col cols="6">
            <v-card outlined rounded="lg" class="pa-4 text-center">
              <div class="kpi-label">Prosjek storna</div>
              <div class="kpi-value">{{ formatEur(stornoAvg) }}</div>
              <div class="kpi-sub">po računu</div>
            </v-card>
          </v-col>
        </v-row>

        <!-- Bar split -->
        <v-card outlined rounded="lg" class="pa-6 mb-6">
          <div class="section-title mb-4">Omjer plaćenih i storniranih</div>

          <div class="mb-4">
            <div class="d-flex justify-space-between mb-1">
              <span style="font-size: 13px; font-weight: 500">Plaćeni</span>
              <span style="font-size: 13px; font-weight: 600; color: #4CAF50">{{ formatEur(paidEuros) }}</span>
            </div>
            <div class="bar-track">
              <div
                class="bar-fill"
                :style="{ width: totalCount > 0 ? (paidCount / totalCount * 100) + '%' : '0%', background: '#4CAF50' }"
              />
            </div>
          </div>

          <div>
            <div class="d-flex justify-space-between mb-1">
              <span style="font-size: 13px; font-weight: 500">Stornirani</span>
              <span style="font-size: 13px; font-weight: 600; color: #F44336">{{ formatEur(stornoEuros) }}</span>
            </div>
            <div class="bar-track">
              <div
                class="bar-fill"
                :style="{ width: totalCount > 0 ? (stornoCount / totalCount * 100) + '%' : '0%', background: '#F44336' }"
              />
            </div>
          </div>
        </v-card>

      </template>
    </div>
  </v-container>
</template>

<script>
import { getFirestore, collection, query, where, getAggregateFromServer, sum, count } from 'firebase/firestore'
import state from '@/state'
import EventBus from '@/plugins/event-bus'

export default {
  name: 'StornoAnaliza',
  inject: ['showMsgBox', 'showLoader', 'hideLoader'],

  data () {
    return {
      margin: '0px',
      targetModifier: '',
      loading: false,
      period: 'month',
      menuFrom: false,
      menuTo: false,
      customFrom: new Date().toISOString().substr(0, 10),
      customTo: new Date().toISOString().substr(0, 10),
      stats: null,
      periods: [
        { value: 'today', label: 'Danas' },
        { value: 'week', label: 'Tjedan' },
        { value: 'month', label: 'Ovaj mjesec' },
        { value: 'custom', label: 'Prilagođeno' }
      ]
    }
  },

  computed: {
    paidCount () { return this.stats?.paidCount || 0 },
    paidEuros () { return this.stats?.paidEuros || 0 },
    stornoCount () { return this.stats?.stornoCount || 0 },
    stornoEuros () { return this.stats?.stornoEuros || 0 },
    totalCount () { return this.paidCount + this.stornoCount },
    stornoPercent () { return this.totalCount > 0 ? this.stornoCount / this.totalCount * 100 : 0 },
    stornoAvg () { return this.stornoCount > 0 ? this.stornoEuros / this.stornoCount : 0 }
  },

  async created () {
    this.margin = state.getMargin()
    const sideNav = this.$route.meta?.sideNav
    if (sideNav === 'Company') this.targetModifier = 'company'
    else if (sideNav === 'PointOfSale') this.targetModifier = 'pointOfSale'
    else if (sideNav === 'CashRegister') this.targetModifier = 'cashRegister'
    EventBus.$on('set-mini', () => { this.margin = state.getMargin() })
    await this.fetchData()
  },

  beforeDestroy () {
    EventBus.$off('set-mini')
  },

  methods: {
    formatEur (v) {
      return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(v)
    },

    formatDate (d) {
      if (!d) return ''
      const [y, m, day] = d.split('-')
      return `${day}.${m}.${y}`
    },

    selectPeriod (val) {
      this.period = val
      if (val !== 'custom') this.fetchData()
    },

    getDateRange () {
      const now = new Date()
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
      const toEndOfDay = new Date(today)
      toEndOfDay.setHours(23, 59, 59)
      if (this.period === 'today') return { from: today, to: toEndOfDay }
      if (this.period === 'week') {
        const day = today.getDay() || 7
        const mon = new Date(today)
        mon.setDate(today.getDate() - day + 1)
        return { from: mon, to: toEndOfDay }
      }
      if (this.period === 'month') {
        return { from: new Date(now.getFullYear(), now.getMonth(), 1), to: toEndOfDay }
      }
      // custom
      if (this.customFrom && this.customTo) {
        return { from: new Date(this.customFrom), to: new Date(this.customTo + 'T23:59:59') }
      }
      return { from: today, to: toEndOfDay }
    },

    async fetchData () {
      this.loading = true
      try {
        const { from, to } = this.getDateRange()
        const fromSec = Math.floor(from.getTime() / 1000)
        const toSec = Math.floor(to.getTime() / 1000)

        const companyId = state.getCurrentCompany().id
        const db = getFirestore()
        const col = collection(db, `receipts/${companyId}/receipts`)

        const [paid, reversed] = await Promise.all([
          getAggregateFromServer(
            query(col,
              where('issue_date_time', '>=', fromSec),
              where('issue_date_time', '<=', toSec),
              where('payment.current_status', '==', 'PAID')
            ),
            { total: sum('total'), cnt: count() }
          ),
          getAggregateFromServer(
            query(col,
              where('issue_date_time', '>=', fromSec),
              where('issue_date_time', '<=', toSec),
              where('payment.current_status', '==', 'REVERSED')
            ),
            { total: sum('total'), cnt: count() }
          )
        ])

        this.stats = {
          paidCount: paid.data().cnt || 0,
          paidEuros: (paid.data().total || 0) / 100,
          stornoCount: reversed.data().cnt || 0,
          stornoEuros: (reversed.data().total || 0) / 100
        }
      } catch (err) {
        console.error('StornoAnaliza:', err)
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.close-btn {
  width: 36px !important;
  height: 36px !important;
  min-width: 36px !important;
  border-radius: 10px !important;
  background: rgba(255, 255, 255, 0.95) !important;
  border: 1px solid rgba(229, 57, 53, 0.2) !important;
  color: #e53935 !important;
  transition: all 0.2s ease !important;
  box-shadow: none !important;
}
.close-btn:hover {
  background: rgba(229, 57, 53, 0.07) !important;
  box-shadow: 0 0 0 3px rgba(229, 57, 53, 0.1) !important;
}
.table-header-filters {
  position: sticky;
  top: 64px;
  z-index: 10;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}
.period-pills {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.period-pill {
  display: inline-flex;
  align-items: center;
  padding: 6px 14px;
  border-radius: 20px;
  border: 1.5px solid #E0E6ED;
  background: #fff;
  font-size: 13px;
  font-weight: 500;
  color: #6B7280;
  cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;
  outline: none;
}
.period-pill:hover {
  border-color: #00BCD4;
  color: #00838F;
  background: rgba(0, 188, 212, 0.05);
}
.period-pill.active {
  border-color: rgba(0, 188, 212, 0.5);
  background: rgba(0, 188, 212, 0.1);
  color: #00838F;
  font-weight: 600;
}
.date-pill {
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.3px;
}
.date-pill-sep {
  font-size: 13px;
  color: #9CA3AF;
  font-weight: 500;
}
.kpi-label { font-size: 12px; color: #6B7280; margin-bottom: 4px; }
.kpi-value { font-size: 22px; font-weight: 700; margin-bottom: 2px; }
.kpi-sub { font-size: 12px; color: #9CA3AF; }
.bar-track { height: 10px; border-radius: 5px; background: #F1F5F9; overflow: hidden; }
.bar-fill { height: 100%; border-radius: 5px; transition: width 0.6s ease; }
.section-title { font-size: 16px; font-weight: 700; color: #2d3748; }
</style>
